import React, {useContext, useEffect, useState} from "react";
import {Autocomplete, Box, Button, FormControl, FormControlLabel, Switch, TextField,} from "@mui/material";
import request from "../../../api/request";
import {BackendAPI} from "../../../const/api";
import "./CreateMaterial.css";
import {QueryOptions} from "../ListPage/OrderList";
import {getOrderQueryOptions} from "../../../services";
import {AuthContext} from "../../../auth/authContext";
import {useToast} from "../../../component/ToastBar";

/**
 * external_id = Column(String)  # 外部订单号
 *     customer_id = Column(VARCHAR(12), nullable=False)  # 客户
 *     created_at = Column(DateTime, default=datetime.now)  # 创建日期
 *     quantity = Column(Integer)  # 数量
 *     unit_price = Column(Float)  # 单价
 *     material_id = Column(VARCHAR(12), nullable=False)
 *     billing_address = Column(String)  # 收货地址
 *     billing_name = Column(String)  # 收货人
 *     billing_tel = Column(String)  # 收货人电话
 *     note = Column(String)  # 备注
 */
const defaultForm = {
  externalId: "",
  customerId: "",
  customerName: "",
  createdAt: "",
  quantity: "",
  unitPrice: "",
  materialId: "",
  billingAddress: "",
  billingName: "",
  billingTel: "",
  note: "",
  needKd: true,
};
const CreateOrder = () => {
  const toast = useToast();
  const [error, setError] = useState("");
  const authContext = useContext(AuthContext);

  const [orderForm, setOrderForm] = useState(defaultForm);
  const [queryOptions, setQueryOptions] = useState<QueryOptions>({
    materials: [],
    customers: [],
    salespersons: [],
  });

  useEffect(() => {
    if (authContext) {
      getOrderQueryOptions(authContext.isCustomer(), authContext.isAdmin()).then(
        ([materials, customers, salespersons]) =>
          setQueryOptions({ materials, customers, salespersons }),
      );
    }
  }, [authContext]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !orderForm.quantity ||
      !orderForm.materialId ||
      !orderForm.billingAddress ||
      !orderForm.billingName ||
      !orderForm.billingTel
    ) {
      setError("必填字段都必须填写");
      return;
    }
    try {
      const res = await request.post(BackendAPI.createOrder, orderForm);
      if (!res.success) {
        return;
      }
      // clear form
      toast(`订单创建成功`);
      setOrderForm(defaultForm);
      setError("");
    } catch (error) {
      console.error("Error create material:", error);
      setError(`订单创建失败: ${error}`);
    }
  };

  return (
    <div className="create-material-form">
      <p>请输入订单信息</p>
      <Box component="form" noValidate onSubmit={handleSubmit}>
        {(authContext?.isAdmin()) && queryOptions.customers && (
          <FormControl
            sx={{ minWidth: 200 }}
            className="input-field"
            key={"customer-select"}
            required
          >
            <Autocomplete
              id={`customer-label`}
              options={queryOptions.customers}
              onChange={(e, value: any) =>
                setOrderForm({ ...orderForm, customerId: value.value })
              }
              renderInput={(params) => (
                <TextField required {...params} label={`客户`} />
              )}
            />
          </FormControl>
        )}
        {(authContext?.isBA() || authContext?.isSalesScopeRole()) && (
          <FormControl
            sx={{ minWidth: 200 }}
            className="input-field"
            key={"customer-select"}
            required
          >
            <TextField
              required
              className="input-field"
              label="客户名称"
              name="customerId"
              value={orderForm.customerName}
              onChange={(e) =>
                setOrderForm({ ...orderForm, customerName: e.target.value })
              }
            />
          </FormControl>
        )}
        <FormControl
          sx={{ minWidth: 200 }}
          className="input-field"
          key={"material-select"}
          required
        >
          <Autocomplete
            id="material-label"
            options={queryOptions.materials}
            onChange={(e, value: any) =>
              setOrderForm({ ...orderForm, materialId: value.value })
            }
            renderInput={(params) => (
              <TextField required {...params} label={`物料`} />
            )}
          />
        </FormControl>
        <TextField
          className="input-field"
          label="外部订单号"
          name="externalId"
          value={orderForm.externalId}
          onChange={(e) =>
            setOrderForm({ ...orderForm, externalId: e.target.value })
          }
        />
        <TextField
          className="input-field"
          label="订单日期"
          name="createdAt"
          type="number"
          value={orderForm.createdAt}
          onChange={(e) =>
            setOrderForm({ ...orderForm, createdAt: e.target.value })
          }
        />
        <div className="input-field tip-info">
          <span>* 日期格式 ：yyyymmdd 为空则默认当前</span>
        </div>
        <TextField
          required
          className="input-field"
          label="数量"
          name="quantity"
          value={orderForm.quantity}
          onChange={(e) => {
            try {
              const number = Number(e.target.value);
              if (isNaN(number)) {
                return;
              }
              setOrderForm({
                ...orderForm,
                quantity: number.toString(),
              });
            } catch (e) {}
          }}
        />
        <TextField
          className="input-field"
          label="单价"
          name="unitPrice"
          type="number"
          value={orderForm.unitPrice}
          onChange={(e) => {
            try {
              const raw = e.target.value;
              if (raw === "") {
                setOrderForm({
                  ...orderForm,
                  unitPrice: "",
                });
                return;
              }
              const number = Number(raw);
              if (isNaN(number)) {
                return;
              }
              setOrderForm({
                ...orderForm,
                unitPrice: number.toString(),
              });
            } catch (e) {}
          }}
        />
        <TextField
          required
          className="input-field"
          label="收件人"
          name="billingName"
          value={orderForm.billingName}
          onChange={(e) =>
            setOrderForm({ ...orderForm, billingName: e.target.value })
          }
        />
        <TextField
          required
          className="input-field"
          label="联系电话"
          name="billingTel"
          value={orderForm.billingTel}
          onChange={(e) =>
            setOrderForm({ ...orderForm, billingTel: e.target.value })
          }
        />
        <TextField
          required
          className="input-field"
          label="收件地址"
          name="billingAddress"
          value={orderForm.billingAddress}
          onChange={(e) =>
            setOrderForm({ ...orderForm, billingAddress: e.target.value })
          }
        />
        <TextField
          className="input-field"
          label="备注"
          name="note"
          value={orderForm.note}
          onChange={(e) => setOrderForm({ ...orderForm, note: e.target.value })}
        />
        {error && (
          <div className="create-material-form error-message">{error}</div>
        )}
        {authContext && !authContext.isCustomer() && (<div>
          <FormControlLabel
            control={
              <Switch
                checked={orderForm.needKd}
                onChange={() =>
                  setOrderForm({
                    ...orderForm,
                    needKd: !orderForm.needKd,
                  })
                }
                name="needKd"
              />
            }
            label="需要上传金蝶"
          />
        </div>)}
        <div>
          <Button className="submit-button" type="submit" variant="contained">
            保存更改
          </Button>
        </div>
      </Box>
    </div>
  );
};

export default CreateOrder;
